// =============================================================================
// Discount helpers
// =============================================================================
// A line discount can be entered EITHER as a percentage of the line gross
// (qty × unit) OR as a flat money amount. Either way the backend stores — and
// invoices/accounting need — an absolute money amount. These helpers convert
// the typed value into that amount and format a % for display.

export type DiscountMode = 'pct' | 'amount';

export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Money amount for a line discount, given the entry mode + typed value. */
export function lineDiscountAmount(qty: number, unitPrice: number, mode: DiscountMode, value: number): number {
  const gross = qty * unitPrice;
  if (gross <= 0) return 0;
  const v = Math.max(0, Number.isFinite(value) ? value : 0);
  if (mode === 'amount') return round2(Math.min(v, gross)); // never exceed the line
  return round2(gross * (v / 100));
}

/**
 * Money amount for an ORDER-level discount, given the entry mode + typed value.
 * `discountableGross` is the summed gross (qty × unit) of the lines that can be
 * discounted — custom-size lines are excluded. Mirrors {@link lineDiscountAmount}
 * but treats the whole order as one bucket.
 */
export function orderDiscountAmount(discountableGross: number, mode: DiscountMode, value: number): number {
  if (discountableGross <= 0) return 0;
  const v = Math.max(0, Number.isFinite(value) ? value : 0);
  if (mode === 'amount') return round2(Math.min(v, discountableGross)); // never exceed the order
  return round2(discountableGross * (v / 100));
}

/**
 * Spread an order-level money discount across lines proportional to each line's
 * gross. The penny remainder from rounding is dropped on the last non-zero line
 * so the parts sum back to `total` exactly. Returns one amount per input gross,
 * in the same order (zero-gross lines — e.g. custom — get 0).
 */
export function allocateDiscount(grosses: number[], total: number): number[] {
  const sum = grosses.reduce((s, g) => s + g, 0);
  if (sum <= 0 || total <= 0) return grosses.map(() => 0);
  const parts = grosses.map((g) => round2((total * g) / sum));
  const drift = round2(total - parts.reduce((s, x) => s + x, 0));
  if (drift !== 0) {
    for (let i = parts.length - 1; i >= 0; i--) {
      if (grosses[i]! > 0) {
        parts[i] = round2(parts[i]! + drift);
        break;
      }
    }
  }
  return parts;
}

/** Back-compute the percentage from a stored money discount (for display). */
export function discountPctFromAmount(qty: number, unitPrice: number, amount: number): number {
  const gross = qty * unitPrice;
  if (gross <= 0) return 0;
  return (amount / gross) * 100;
}

/** "10%", "7.5%", "0%" — one decimal max, trailing .0 stripped. */
export function formatPct(pct: number): string {
  if (!Number.isFinite(pct) || pct <= 0) return '0%';
  const r = Math.round(pct * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}

/** Clamp a typed percentage into the 0–100 range. */
export const clampPct = (n: number): number => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));
